import { useCallback, useState } from 'react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import Draggable from 'react-draggable';
import { ChatContainer, MessageList, Message, MessageInput, TypingIndicator, ConversationHeader, Avatar } from '@chatscope/chat-ui-kit-react';

import styled from '@emotion/styled';

import ElementIcon from '../ElementIcon';
import useLoader from '../../util/useLoader';

import FormattedMessage from './FormattedMessage';
import { keyframes } from '@emotion/react';
import Notification from '../Notification';
import { themeColorLevel } from 'src/styles/bootstrap/scss/_functions';
import { mix } from 'polished';

function AIChatModal({ agent, agentTyping, agentError, chatContext, messages, onSend, open, onClose, onClear }) {
  const [input, setInput] = useState('');
  const handleSend = useCallback(
    input => {
      setInput('');
      onSend(input);
    },
    [onSend]
  );

  return (
    <DraggableBox>
      <AIChatModalDiv className={open ? 'is-open' : 'is-closed'}>
        <ChatContainer>
          <ConversationHeader className="drag-handle">
            <Avatar className="avatar">
              <ElementIcon element={agent.icon} size="lg" fontSize="1.35em" />
            </Avatar>
            <ConversationHeader.Content>{agent.name}</ConversationHeader.Content>
            <ConversationHeader.Actions>
              <div className="align-right">
                {messages?.length > 0 ? (
                  <Button color="link" title="Clean Context" onClick={onClear}>
                    <i className="fa fa-trash" />
                  </Button>
                ) : null}
                <Button color="link" title="Close" onClick={onClose}>
                  <i className="fa fa-times" />
                </Button>
              </div>
            </ConversationHeader.Actions>
          </ConversationHeader>

          <MessageList
            className="no-drag"
            typingIndicator={agentTyping ? <TypingIndicator content={`${agent.name} is typing`} /> : agentError ? <div>agentError</div> : null}
          >
            {messages.map((message, index) => (
              <FormattedMessage key={index} message={message} chatContext={chatContext} />
            ))}
          </MessageList>
          <MessageInput
            className="no-drag"
            placeholder="Type message here"
            value={input}
            attachButton={false}
            disabled={agentTyping}
            onChange={val => setInput(val)}
            onSend={handleSend}
          />
        </ChatContainer>
      </AIChatModalDiv>
      {/* 
      <Modal isOpen container="body"
        className="modal-primary maximized"
        toggle={dispatch.toggleModal}
        backdrop
      >
        <ModalHeader
          toggle={dispatch.toggleModal}
        >
          {label}
        </ModalHeader>
        <ModalBody>
          <JnxFieldComponent modal
            value={value} object={object} onChange={onChange}
            state={state} dispatch={dispatch}
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={dispatch.toggleModal}>Close Modal</Button>
        </ModalFooter>
      </Modal> */}
    </DraggableBox>
  );
}

function DraggableBox({ handle, children }) {
  return (
    <Draggable bounds={{ bottom: 0, right: 0 }} handle=".drag-handle">
      <DraggableDiv>{children}</DraggableDiv>
    </Draggable>
  );
}

const DraggableDiv = styled('div')({
  position: 'absolute',
  bottom: '0%',
  right: '2%',
  zIndex: '900020',
  boxShadow: ' 0px 5px 10px rgba(0, 0, 0, 0.25)z',
  '.drag-handle': {
    cursor: 'grab',
  },
  '&.react-draggable-dragging': {
    boxShadow: ' 0px 5px 10px rgba(0, 0, 0, 0.25), 0px 15px 25px rgba(0, 0, 0, 0.2)',
    '.drag-handle': {
      cursor: 'move',
    },
  },
});

const AIChatModalDiv = styled('div')(({ theme }) => {
  const modalBorderColor = mix(theme.modalBackdropOpacity, theme.modalBackdropBg, theme.bodyBg);
  const outgoingBubbleColor = '#6ea9d7';
  const incomingBubbleColor = '#d8e0e6';

  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    height: '500px',
    border: `1px solid ${modalBorderColor}`,

    '.cs-chat-container': {
      display: 'flex',
      flexDirection: 'column',
      background: theme.bodyBg,
      flexGrow: '1',
      overflow: 'hidden',
      minWidth: '400px !important',
      maxWidth: '400px',
      borderRadius: `${theme.borderRadius} ${theme.borderRadius} 0px 0px`,
      position: 'relative',
    },

    '.cs-conversation-header': {
      cursor: 'move',
      fontFamily: theme.fontFamily,
      backgroundColor: theme.primaryMain,
      color: theme.primaryTextMain,
      paddingLeft: '5px',
      paddingRight: '5px',
      fontSize: '1rem',

      '> .cs-conversation-header__avatar': {
        marginRight: '8px',
      },
      '> .cs-conversation-header__content': {
        // "fontSize": "1rem",
        fontWeight: 'bold',
      },

      '> .cs-conversation-header__actions': {
        width: 'auto',
        display: 'flex !important',
        justifyContent: 'flex-end !important',
        button: {
          opacity: '0.8',
          '&:hover': {
            opacity: '1',
          },
          '> *': {
            backgroundColor: theme.primaryMain,
            color: theme.primaryTextMain,
          },
        },
      },
    },

    '.cs-message-list__scroll-wrapper': {
      paddingLeft: '10px',
      paddingRight: '10px',
    },

    '.cs-message': {
      marginTop: '16px',
      '&.cs-message--outgoing .cs-message__content': {
        marginRight: '8px',
        backgroundColor: outgoingBubbleColor,
      },
      '&.cs-message--incoming .cs-message__content': {
        marginLeft: '8px',
        backgroundColor: incomingBubbleColor,
      },

      '&.is-turn-start': {
        '&.cs-message--outgoing .cs-message__content': {
          position: 'relative',
          borderTopRightRadius: '4px',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: '-8px',
            top: '0',
            width: '0',
            height: '0',
            borderStyle: 'solid',
            borderWidth: '8px',
            borderColor: `${outgoingBubbleColor} transparent transparent transparent`,
            borderTopRightRadius: '4px',
          },
        },
        '&.cs-message--incoming .cs-message__content': {
          position: 'relative',
          borderTopLeftRadius: '4px',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '-8px',
            top: '0',
            width: '0',
            height: '0',
            borderStyle: 'solid',
            borderWidth: '8px',
            borderColor: `${incomingBubbleColor} transparent transparent transparent`,
            borderTopLeftRadius: '4px',
          },
        },
      },

      '&.is-continuation': {
        marginTop: '4px',
      },

      '&.is-error': {
        marginTop: '4px',
        'max-width': '100%',
        '> * > .cs-message__content': {
          marginRight: '0',
          marginLeft: '0',
          backgroundColor: themeColorLevel(theme, 'danger', theme.alertBgLevel),
          borderColor: themeColorLevel(theme, 'danger', theme.alertBorderLevel),
          color: themeColorLevel(theme, 'danger', theme.alertColorLevel),
          borderRadius: theme.alertBorderRadius,
        },
      },

      '.cs-message__content': {
        borderRadius: '35px',
      },

      ['.cs-message__custom-content strong, ' + '.markdown-content, ' + '.cs-message-input__content-editor, ' + '.cs-typing-indicator__text']: {
        fontSize: '14px !important',
        a: {
          color: theme.linkColor,
          fontSize: '14px !important',
        },
      },
    },

    '.cs-avatar': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    '.cs-message__custom-content': {
      'ul, ol': {
        margin: '0',
        padding: '0',
      },
      li: {
        margin: '0',
        padding: '0',
        lineHeight: '0.7',
        listStyle: 'none',
      },
      strong: {
        fontWeight: 'bold',
      },
      em: {
        fontStyle: 'italic',
      },
    },

    '.cs-message-input__content-editor-wrapper': {
      backgroundColor: theme.white,
      borderRadius: '4px',
      '> .cs-message-input__content-editor-container': {
        backgroundColor: 'transparent',
        '> .cs-message-input__content-editor': {
          backgroundColor: 'transparent',
        },
      },
    },
  };
});

export default AIChatModal;
